import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import API_URLS from "../../../core/common/apiUrls";
import { all_routes } from "../../router/all_routes";
import HotelFilter from "./HotelFilter";
import HotelLoader from "../HotelLoader";
import "../hotel-loader.css";
// ===== Helpers to support BOT → /hotel-grid (new tab) =====
const readTriobotState = () => {
  const KEYS = ["triobot_hotel_grid_state", "triobot_hotel_grid"];
  for (const key of KEYS) {
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      if (parsed?.hotelSearchDetails) return parsed;
      if (parsed?.searchPayload) {
        const sp = parsed.searchPayload;
        return {
          hotelSearchDetails: {
            city: { cityCode: sp.CityCode, name: "", country: "" },
            checkInDate: sp.CheckIn,
            checkOutDate: sp.CheckOut,
            PaxRooms: sp.PaxRooms,
          },
        };
      }
    } catch {}
  }
  return null;
};

const consumeTriobotState = () => {
  const s = readTriobotState();
  if (s) {
    try { sessionStorage.removeItem("triobot_hotel_grid_state"); } catch {}
    try { sessionStorage.removeItem("triobot_hotel_grid"); } catch {}
  }
  return s;
};

// Grouped facility categories
const facilityCategories = [
  { key: "wifi", label: "WiFi", keywords: ["wifi", "internet"], icon: "fa-wifi" },
  { key: "parking", label: "Parking", keywords: ["parking", "valet", "self parking", "free parking"], icon: "fa-car" },
  { key: "pool", label: "Swimming Pool", keywords: ["swimming pool", "pool", "outdoor pool"], icon: "fa-swimming-pool" },
  { key: "bar", label: "Bar", keywords: ["bar", "poolside bar", "bars/lounges"], icon: "fa-glass-martini-alt" },
  { key: "roomService", label: "Room Service", keywords: ["room service"], icon: "fa-concierge-bell" },
  { key: "laundry", label: "Laundry", keywords: ["laundry", "dry cleaning"], icon: "fa-soap" },
  { key: "saunaSteam", label: "Sauna / Steam", keywords: ["sauna", "steam room"], icon: "fa-hot-tub" },
];

const extractStarRating = (rating: string | undefined): number => {
  if (!rating) return 0;
  const s = rating.toString().toLowerCase();
  if (s.includes("one")) return 1;
  if (s.includes("two")) return 2;
  if (s.includes("three")) return 3;
  if (s.includes("four")) return 4;
  if (s.includes("five")) return 5;
  const n = parseInt(rating as any, 10);
  return isNaN(n) ? 0 : n;
};

const fallbackHotels = [
  {
    Name: "Fallback Hotel 1",
    HotelCode: "FB001",
    StarRating: "FourStar",
    Price: 15000,
    Currency: "INR",
    City: "Mumbai",
    Country: "India",
    Address: "Marine Drive, Mumbai",
    Images: ["https://via.placeholder.com/200"],
    HotelFacilities: ["Free WiFi", "Self Parking", "Swimming Pool"],
    Latitude: "19.076",
    Longitude: "72.8777",
  },
];

const HotelGrid: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const searchState = useMemo(() => {
    const routerState = (location.state as any) || {};
    const botState = !routerState.hotelSearchDetails ? consumeTriobotState() : null;
    return (routerState.hotelSearchDetails || botState?.hotelSearchDetails) as any;
  }, [location.state]);

  const [hotels, setHotels] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortOption, setSortOption] = useState<string>("recommended");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalHotels, setTotalHotels] = useState(0);

  const [selectedStarRatings, setSelectedStarRatings] = useState<number[]>([]);
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 100000]);

  const abortControllerRef = useRef<AbortController | null>(null);
  const paxRooms: any[] = useMemo(() => {
    if (!searchState) return [];
    if (Array.isArray(searchState.PaxRooms) && searchState.PaxRooms.length > 0) {
      return searchState.PaxRooms.map((r: any) => ({
        Adults: Number(r.Adults) || 0,
        Children: Number(r.Children) || 0,
        ChildrenAges: Array.isArray(r.ChildrenAges) ? r.ChildrenAges : [],
      }));
    }
    return [
      {
        Adults: Number(searchState.adults) || 1,
        Children: Number(searchState.children) || 0,
        ChildrenAges: [],
      },
    ];
  }, [searchState]);

  const totals = useMemo(() => {
    if (!paxRooms || paxRooms.length === 0) {
      return { adults: searchState?.adults || 0, children: searchState?.children || 0 };
    }
    return paxRooms.reduce(
      (acc: any, r: any) => {
        acc.adults += Number(r.Adults) || 0;
        acc.children += Number(r.Children) || 0;
        return acc;
      },
      { adults: 0, children: 0 }
    );
  }, [paxRooms, searchState]);

  const fetchHotels = async () => {
  console.log("🚀 fetchHotels called");

  if (!searchState) {
    console.log("⛔ searchState missing");
    return;
  }
  console.log("📦 Fetch payload inputs:");
  console.log("CityCode:", searchState.city.cityCode);
  console.log("CheckIn:", searchState.checkInDate);
  console.log("CheckOut:", searchState.checkOutDate);
  console.log("Page:", currentPage);
  console.log("StarFilters:", selectedStarRatings);
  console.log("PaxRooms:", paxRooms);
  abortControllerRef.current?.abort();

  const controller = new AbortController();
  abortControllerRef.current = controller;

  setLoading(true);

  try {
    const payload: any = {
      CityCode: searchState.city.cityCode,
      Language: "EN",
      CheckIn: searchState.checkInDate,
      CheckOut: searchState.checkOutDate,
      GuestNationality: "IN",
      PaxRooms: paxRooms,
      ResponseTime: 20,
      IsDetailedResponse: true,
      Page: currentPage,
      PageSize: 20,
      Filters: {
        Refundable: false,
        NoOfRooms: 0,
      },
    };

    if (selectedStarRatings.length > 0) {
      payload.Filters.StarRatings = selectedStarRatings;
    }

    const response = await fetch(API_URLS.HOTELS.HOTEL_SEARCH, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });

    const data = await response.json();
    console.log("✅ API response received");
    console.log("Hotels count:", data?.hotels?.length);
    console.log("Total pages:", data?.total_pages);
    if (data?.hotels) {
       console.log("📊 Updating hotel state");
      setHotels(data.hotels);
      setTotalPages(data.total_pages || 1);
      setTotalHotels(data.total || data.hotels.length);
      setError(null);
      } else {
        setHotels([]);
      }
    } catch (err: any) {
    if (err.name !== "AbortError") {
      console.error(err);
      setError("Failed to load hotels.");
    }
  } finally {
    setLoading(false);
  }
}

useEffect(() => {
  console.log("🔁 useEffect triggered");
  console.log("searchState:", searchState);
  console.log("currentPage:", currentPage);
  console.log("selectedStarRatings:", selectedStarRatings);

  if (!searchState) {
    console.log("⛔ searchState missing, skipping fetch");
    return;
  }

  fetchHotels();
}, [searchState, currentPage, selectedStarRatings]);

// Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const handleFilterChange = (newFilters: { starRatings: number[]; priceRange: [number, number] }) => {
    console.log("🎛 Filter change triggered");
    console.log("New star ratings:", newFilters.starRatings);
    console.log("New price range:", newFilters.priceRange);
    setSelectedStarRatings(newFilters.starRatings);
    setPriceRange(newFilters.priceRange);
    setCurrentPage(1);
  };

  const filteredByPrice = useMemo(() => {
  return hotels.filter((hotel) => {
    const star = extractStarRating(hotel.StarRating);

    const starMatch =
      selectedStarRatings.length === 0 ||
      selectedStarRatings.includes(star);

    const priceMatch =
      hotel.Price >= priceRange[0] &&
      hotel.Price <= priceRange[1];

    return starMatch && priceMatch;
  });
}, [hotels, priceRange, selectedStarRatings]);

  const sortedHotels = useMemo(() => {
    const hotelsCopy = [...filteredByPrice];
    switch (sortOption) {
      case "priceLowHigh":
        return hotelsCopy.sort((a, b) => a.Price - b.Price);
      case "priceHighLow":
        return hotelsCopy.sort((a, b) => b.Price - a.Price);
      case "starHighLow":
        return hotelsCopy.sort(
          (a, b) => extractStarRating(b.StarRating) - extractStarRating(a.StarRating)
        );
      case "starLowHigh":
        return hotelsCopy.sort(
          (a, b) => extractStarRating(a.StarRating) - extractStarRating(b.StarRating)
        );
      default:
        return hotelsCopy;
    }
  }, [filteredByPrice, sortOption]);

  const renderStars = (count: number) =>
    Array.from({ length: count }, (_, i) => (
      <i key={i} className="fa fa-star text-warning me-1"></i>
    ));

  const openInMaps = (lat: string, lng: string) => {
    const url = `https://www.google.com/maps?q=${lat},${lng}`;
    window.open(url, "_blank");
  };

  const renderFacilities = (facilities: string[]) => {
    const normalized = facilities.map((f) => (f || "").toLowerCase());
    const icons: React.ReactNode[] = [];
    facilityCategories.forEach((cat) => {
      const matches = normalized.filter((f) =>
        cat.keywords.some((kw) => f.includes(kw))
      );
      if (matches.length > 0) {
        icons.push(
          <span key={cat.key} className="me-2" title={matches.join(", ")}>
            <i className={`fa ${cat.icon} text-muted`}></i>
          </span>
        );
      }
    });
    return icons;
  };

  if (loading) {
  return (
    <div className="hotel-loading">
      <HotelLoader/>
    </div>
  );
}

  if (error) {
    return (
      <div className="hotel-error text-center py-5">
        <i className="isax isax-close-circle fs-40 text-danger mb-3"></i>
        <h4>Something went wrong</h4>
        <p>{error}</p>
        <button className="btn btn-primary mt-3" onClick={() => navigate(-1)}>
          Go Back
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="breadcrumb-bar flightList-banner-bg text-center">
        <div className="container">
          <h2 className="breadcrumb-bar flightList-banner-bg text-center">
            {searchState?.city?.name || "Hotels"}
            {searchState?.city?.country ? `, ${searchState.city.country}` : ""}
          </h2>
          <p className="text-white">
            {searchState?.checkInDate || "-"} → {searchState?.checkOutDate || "-"} |{" "}
            {totals.adults} Adults, {totals.children} Children
          </p>
        </div>
      </div>

      <div className="content">
        <div className="container">
          <div className="row">
            <div className="col-xl-3 col-lg-4">
            <HotelFilter
  hotels={hotels}
  starRatings={selectedStarRatings}
  priceRange={priceRange}
  onFilterChange={handleFilterChange}
/>
          </div>

            <div className="col-xl-9 col-lg-8">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h6 className="mb-0">{totalHotels} Hotels Found</h6>
                <div className="dropdown">
                  <button
                    className="btn btn-sm btn-outline-primary dropdown-toggle"
                    type="button"
                    data-bs-toggle="dropdown"
                  >
                    Sort By
                  </button>
                  <ul className="dropdown-menu dropdown-menu-end">
                    <li><button className="dropdown-item" onClick={() => setSortOption("recommended")}>Recommended</button></li>
                    <li><button className="dropdown-item" onClick={() => setSortOption("priceLowHigh")}>Price: Low → High</button></li>
                    <li><button className="dropdown-item" onClick={() => setSortOption("priceHighLow")}>Price: High → Low</button></li>
                    <li><button className="dropdown-item" onClick={() => setSortOption("starHighLow")}>Star Rating: High → Low</button></li>
                    <li><button className="dropdown-item" onClick={() => setSortOption("starLowHigh")}>Star Rating: Low → High</button></li>
                  </ul>
                </div>
              </div>

              {sortedHotels.map((hotel, index) => (
                <div key={index} className="card hotel-card mb-4 shadow-sm">
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-3">
                        <div id={`carousel-${index}`} className="carousel slide" data-bs-ride="carousel">
                          <div className="carousel-inner">
                            {(hotel.Images || []).slice(0, 5).map((img: string, i: number) => (
                              <div key={i} className={`carousel-item ${i === 0 ? "active" : ""}`}>
                                <img
                                  src={img}
                                  className="d-block w-100 rounded"
                                  alt={`Hotel ${i}`}
                                  style={{ height: "200px", objectFit: "cover" }}
                                />
                              </div>
                            ))}
                          </div>
                          {hotel.Images?.length > 1 && (
                            <>
                              <button className="carousel-control-prev" type="button" data-bs-target={`#carousel-${index}`} data-bs-slide="prev">
                                <span className="carousel-control-prev-icon"></span>
                              </button>
                              <button className="carousel-control-next" type="button" data-bs-target={`#carousel-${index}`} data-bs-slide="next">
                                <span className="carousel-control-next-icon"></span>
                              </button>
                            </>
                          )}
                        </div>
                      </div>

                      <div className="col-md-6">
                        <h5 className="mb-1 d-flex align-items-center">
                          <Link
                            to={all_routes.hotelDetails}
                            state={{
                              hotelCode: hotel.HotelCode,
                              city: hotel.City,
                              country: hotel.Country,
                              checkIn: searchState?.checkInDate,
                              checkOut: searchState?.checkOutDate,
                              paxRooms: paxRooms,
                              images: hotel.Images,
                            }}
                          >
                            {hotel.Name}
                          </Link>
                          <button
                            className="btn btn-sm btn-outline-secondary ms-2"
                            onClick={() => openInMaps(hotel.Latitude, hotel.Longitude)}
                            title="Get Directions"
                          >
                            <i className="fa fa-map-marker-alt"></i>
                          </button>
                        </h5>
                        <p className="text-muted mb-1">
                          {renderStars(extractStarRating(hotel.StarRating))}
                        </p>
                        <p className="fs-14 text-gray mb-2">{hotel.Address}</p>
                        <div className="mb-2">
                          {renderFacilities(hotel.HotelFacilities || [])}
                        </div>
                        <span className="badge bg-info bg-opacity-10 text-info fs-12 me-2">{hotel.City}</span>
                        <span className="badge bg-success bg-opacity-10 text-success fs-12">{hotel.Country}</span>
                      </div>

                      <div className="col-md-3 text-end">
                        <div className="fw-bold fs-20 text-primary mb-2">
                          {hotel.Currency} {Number(hotel.Price).toLocaleString()}
                        </div>
                        <small className="text-muted">Per night</small>
                        <div className="mt-3">
                          <Link
                            to={all_routes.hotelDetails}
                            className="btn btn-primary btn-sm"
                            state={{
                              hotelCode: hotel.HotelCode,
                              city: hotel.City,
                              country: hotel.Country,
                              checkIn: searchState?.checkInDate,
                              checkOut: searchState?.checkOutDate,
                              paxRooms: paxRooms,
                              images: hotel.Images,
                            }}
                          >
                            View Details
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {sortedHotels.length === 0 && (
                <div className="alert alert-info">No hotels match your filter criteria.</div>
              )}

              {totalPages > 1 && (
                <div className="d-flex justify-content-center align-items-center mt-4 mb-4">
                  <button
                    className="btn btn-outline-primary me-3"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((prev) => prev - 1)}
                  >
                    ← Previous
                  </button>
                  <span className="fw-semibold">Page {currentPage} of {totalPages}</span>
                  <button
                    className="btn btn-outline-primary ms-3"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((prev) => prev + 1)}
                  >
                    Next →
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HotelGrid;